import { flyAndSailDestinationNames, routesFromIsland, scillonianDestinationNames, skybusDestinationNames, sailAndFlyDestinationNames } from '@/config/global.strings';
import { DirectionEnum, ModeOfTravelEnum, PassengerTypeEnum, TicketTypeEnum } from '@/models/enums';
import { AddressInformationState, BookingPartState, BookingState, DepartureState, PassengerState, ResourceState, TermState, TicketTypeState } from '@/models/www/store/BookingState';
import { stateStorage } from '@/services/storage.service';
import { defineStore } from 'pinia';
import { useUserStore } from "@/store/user";

export const useBookingStore = defineStore('booking', {
    state: () => ({
        booking: {
            amendmentMode: false,
            bookingNumber: 0,
            modeOfTravel: ModeOfTravelEnum.Scillonian,
            tripType: DirectionEnum.Return,
            addressInformation: {} as AddressInformationState,
            leadPassengerId: "",
            sendConfirmation: false,
            sendConfirmationCopy: true,
            terms: [],
            outward: {
                isLocked: false,
                from: "",
                to: "",
                route: "",
                date: undefined,
                concessionNumber: undefined,
                passengers: [{
                    type: useUserStore().getIsShareholder ? PassengerTypeEnum.ShareholderAdult : PassengerTypeEnum.Adult
                }] as PassengerState[],
                departure: {
                    id: "",
                    identifier: "",
                    ticketType: {} as TicketTypeState
                } as DepartureState,
                resources: []
            } as BookingPartState,
            return: {
                isLocked: false,
                from: "",
                to: "",
                route: "",
                date: undefined,
                concessionNumber: undefined,
                passengers: [{
                    type: useUserStore().getIsShareholder ? PassengerTypeEnum.ShareholderAdult : PassengerTypeEnum.Adult
                }] as PassengerState[],
                departure: {
                    id: "",
                    identifier: "",
                    ticketType: {} as TicketTypeState
                } as DepartureState,
                resources: []
            } as BookingPartState,
        } as BookingState
    }),
    getters: {
        getBooking(): BookingState {
            return this.booking as BookingState
        },
        getTerm: (state) => (identifier: string | undefined): TermState => {
            return state.booking.terms.find(x => x.identifier === identifier) ?? {} as TermState;
        },

        getIsLocked: (state) => (direction: DirectionEnum): boolean => {
            if (direction === DirectionEnum.Outward) {
                return state.booking.outward.isLocked;
            }
            else {
                return state.booking.return.isLocked;
            }
        },
        getResource: (state) => (direction: DirectionEnum, code: string): ResourceState => {
            if (direction === DirectionEnum.Outward) {
                return state.booking.outward.resources?.find(x => x.code === code) ?? {} as ResourceState
            }
            else {
                return state.booking.return.resources?.find(x => x.code === code) ?? {} as ResourceState
            }
        },
        getResources: (state) => (direction: DirectionEnum): ResourceState[] => {
            if (direction === DirectionEnum.Outward) {
                return state.booking.outward.resources;
            }
            else {
                return state.booking.return.resources;
            }
        },
        getAllResources: (state) => (): ResourceState[] => {
            return state.booking.outward.resources.concat(state.booking.return.resources);
        },
        getPassengers: (state) => (direction: DirectionEnum): PassengerState[] => {
            if (direction === DirectionEnum.Outward) {
                return state.booking.outward.passengers as PassengerState[];
            }
            else {
                return state.booking.return.passengers as PassengerState[];
            }
        },
        getDeparture: (state) => (direction: DirectionEnum): DepartureState => {
            if (direction === DirectionEnum.Outward) {
                return state.booking.outward.departure;
            } else {
                return state.booking.return.departure;
            }
        },
        getRoute: (state) => (direction: DirectionEnum): string => {
            if (direction === DirectionEnum.Outward) {
                return state.booking.outward.route;
            }
            return state.booking.return.route;
        },
        getDate: (state) => (directionType: DirectionEnum): Date | undefined => {
            if (directionType === DirectionEnum.Outward) {
                return state.booking.outward.date ? new Date(state.booking.outward.date as Date) : undefined;
            }

            return state.booking.return.date ? new Date(state.booking.return.date as Date) : undefined;
        },
        getDestinationNames: (state) => {
            switch (state.booking.modeOfTravel) {
                case ModeOfTravelEnum.Scillonian:
                    return scillonianDestinationNames;
                case ModeOfTravelEnum.Skybus:
                    return skybusDestinationNames;
                case ModeOfTravelEnum.FlyAndSail:
                    return flyAndSailDestinationNames;
                case ModeOfTravelEnum.SailAndFly:
                    return sailAndFlyDestinationNames;
                default:
                    return scillonianDestinationNames;
            }
        },
        getRouteIsFromIsland: (state) => {
            return routesFromIsland.includes(state.booking.outward.route);
        }
    },
    actions: {
        setBooking(booking: BookingState) {
            this.booking = booking;
        },
        setBookingNumber(bookingNumber: number) {
            this.booking.bookingNumber = bookingNumber;
        },
        setModeOfTravel(modeOfTravel: ModeOfTravelEnum) {
            this.booking.modeOfTravel = modeOfTravel;
        },
        setTripType(tripType: DirectionEnum) {
            this.booking.tripType = tripType;
        },
        setTerm(term: TermState) {
            if (term.checked) {
                this.addTerm(term);
            }
            else {
                this.removeTerm(term);
            }
        },
        setCustomerEmail(email: string) {
        		this.booking.addressInformation.email = email;
        },
        setSendConfirmation(send: boolean) {
        		this.booking.sendConfirmation = send;
        },
        setSendConfirmationCopy(send: boolean) {
        	this.booking.sendConfirmationCopy = send;
        },
        addTerm(term: TermState) {
            this.booking.terms.push(term);
        },
        removeTerm(term: TermState) {
            this.booking.terms = this.booking.terms.filter(t => t.identifier !== term.identifier);
        },
        setLeadPassenger(nameListId?: string) {
            if (nameListId) {
                this.booking.leadPassengerId = nameListId;
            }
        },
        setAddressInformation(addressInformation: AddressInformationState) {
            this.booking.addressInformation = addressInformation;
        },
        setFromDestination(destination: string, modeOfTravel: ModeOfTravelEnum) {
            this.booking.outward.from = destination;

            if (modeOfTravel === ModeOfTravelEnum.FlyAndSail ||
                modeOfTravel === ModeOfTravelEnum.SailAndFly) {
                this.booking.return.from = destination;
            }
            else if (this.booking.tripType === DirectionEnum.Return) {
                this.booking.return.from = this.booking.outward.to;
            }

            this.setRoutes(modeOfTravel);
        },
        setToDestination(destination: string, modeOfTravel: ModeOfTravelEnum) {
            this.booking.outward.to = destination;

            if (modeOfTravel === ModeOfTravelEnum.FlyAndSail ||
                modeOfTravel === ModeOfTravelEnum.SailAndFly) {
                this.booking.return.to = destination;
            }
            else if (this.booking.tripType === DirectionEnum.Return) {
                this.booking.return.to = this.booking.outward.from;
            }

            this.setRoutes(modeOfTravel);
        },
        setDate(directionType: DirectionEnum, date?: Date) {
            if (directionType === DirectionEnum.Outward) {
                this.booking.outward.date = date;
            }
            else {
                this.booking.return.date = date;
            }
        },
        setRoutes(modeOfTravel: ModeOfTravelEnum) {
            //if fly and sail is set we don't need to build up the 
            //route string since it already is pre-defined
            if (modeOfTravel === ModeOfTravelEnum.FlyAndSail ||
                modeOfTravel === ModeOfTravelEnum.SailAndFly) {
                this.booking.outward.route = this.booking.outward.from;
                this.booking.return.route = this.booking.return.to;
            }
            else {
                this.booking.outward.route = this.booking.outward.from + this.booking.outward.to;

                if (this.booking.tripType === DirectionEnum.Return) {
                    this.booking.return.route = this.booking.outward.to + this.booking.outward.from;
                }
            }
        },
        setDeparture(direction: DirectionEnum, departure: DepartureState) {
            if (direction === DirectionEnum.Outward) {
                this.booking.outward.departure = departure;
            } else {
                this.booking.return.departure = departure;
            }
        },
        clearDeparture(direction: DirectionEnum) {
            if (direction === DirectionEnum.Outward) {
                this.booking.outward.departure = {
                    id: "",
                    identifier: "",
                    type: TicketTypeEnum.Standard,
                    ticketType: {} as TicketTypeState
                }
            }
            else {
                this.booking.return.departure = {
                    id: "",
                    identifier: "",
                    type: TicketTypeEnum.Standard,
                    ticketType: {} as TicketTypeState
                }
            }
        },
        setDepartureTicketType(direction: DirectionEnum, ticketType: TicketTypeState) {
            if (direction === DirectionEnum.Outward) {
                this.booking.outward.departure.identifier = ticketType.identifier;
                this.booking.outward.departure.ticketType = ticketType;
            } else {
                this.booking.return.departure.identifier = ticketType.identifier;
                this.booking.return.departure.ticketType = ticketType;
            }
        },
        setResource(direction: DirectionEnum, value: number, resource: ResourceState) {
            const resourceArray = this.getResources(direction);
            const count = resourceArray.filter(x => x.code === resource.code).length;

            if (count > value) {
                this.removeResource(direction, value, resource);
            }
            else {
                this.addResource(direction, value, resource);
            }
        },
        updateResources(direction: DirectionEnum, resources: ResourceState[]) {
            if (direction === DirectionEnum.Outward) {
                this.booking.outward.resources = resources;
            }
            else {
                this.booking.return.resources = resources;
            }
        },
        removeResource(direction: DirectionEnum, value: number, resource: ResourceState) {
            if (direction === DirectionEnum.Outward && this.booking.outward.resources.length > 0) {
                this.booking.outward.resources = this.booking.outward.resources.filter(r => r.id <= value || r.code !== resource.code);
            }
            else if (direction === DirectionEnum.Return && this.booking.return.resources.length > 0) {
                this.booking.return.resources = this.booking.return.resources.filter(r => r.id <= value || r.code !== resource.code);
            }
        },
        addResource(direction: DirectionEnum, value: number, resource: ResourceState) {
            if (value === 0) return;

            for (let i = 0; i < value; i++) {
                if (direction === DirectionEnum.Outward) {
                    if (!this.booking.outward.resources.find(r => r.identifier === resource.identifier && r.id === i + 1)) {
                        this.booking.outward.resources.push({ 
                            identifier: resource.identifier, 
                            id: i + 1, 
                            code: resource.code,
                            route: resource.route
                        } as ResourceState)
                    }
                }
                else {
                    if (!this.booking.return.resources.find(r => r.identifier === resource.identifier && r.id === i + 1)) {
                        this.booking.return.resources.push({ 
                            identifier: resource.identifier, 
                            id: i + 1, 
                            code: resource.code,
                            route: resource.route
                        } as ResourceState)
                    }
                }
            }
        },
        resetFromDestination() {
            this.booking.outward.from = "";
        },
        resetToDestination() {
            this.booking.outward.to = "";
        },
        clearReturnRoute() {
            this.booking.return.route = "";
            this.booking.return.from = "";
            this.booking.return.to = "";
        },
        clearOutwardRoute() {
            this.booking.outward.route = "";
            this.booking.outward.from = "";
            this.booking.outward.to = "";
        },
        clearRoutes() {
            this.clearOutwardRoute();
            this.clearReturnRoute();
        },
        setPassengers(direction: DirectionEnum, passengers: PassengerState[]) {
            for (let i = 0; i < passengers.length; i++) {
                //re populate id for passengers
                passengers[i].id = i + 1;
            }

            if (direction === DirectionEnum.Outward) {
                this.booking.outward.passengers = passengers;
            }
            else {
                this.booking.return.passengers = passengers;
            }
        },
        setPassenger(direction: DirectionEnum, type: PassengerTypeEnum, value: number) {
            let passengerAmount = 0;
            if (direction === DirectionEnum.Outward) {
                passengerAmount = this.booking.outward.passengers.filter(p => p.type === type).length;
            }
            else {
                passengerAmount = this.booking.outward.passengers.filter(p => p.type === type).length;
            }

            if (passengerAmount > value) {
                this.removePassenger(direction, type, value);
            }
            else if (passengerAmount !== value) {
                this.addPassenger(direction, type, value);
            }
        },
        addPassenger(direction: DirectionEnum, type: PassengerTypeEnum, value: number) {
            if (value === 0) return;

            if (direction === DirectionEnum.Outward) {
                if (!this.booking.outward.passengers.find(p => p.type === type && p.id === value)) {
                    this.booking.outward.passengers.push({ type: type, id: this.booking.outward.passengers.filter(p => p.type === type).length + 1 });
                }
            }
            else {
                if (!this.booking.return.passengers.find(p => p.type === type && p.id === value)) {
                    this.booking.return.passengers.push({ type: type, id: this.booking.return.passengers.filter(p => p.type === type).length + 1 });
                }
            }
        },
        updatePassenger(direction: DirectionEnum, passenger: PassengerState) {
            if (direction === DirectionEnum.Outward) {
                if (passenger.nameListId) {
                    const index = this.booking.outward.passengers.findIndex(p => p.nameListId === passenger.nameListId);
                    this.booking.outward.passengers.splice(index, 1, passenger);
                }
            } else {
                if (passenger.nameListId) {
                    const index = this.booking.return.passengers.findIndex(p => p.nameListId === passenger.nameListId)
                    this.booking.return.passengers.splice(index, 1, passenger);
                }
            }
        },
        removePassenger(direction: DirectionEnum, type: PassengerTypeEnum, value: number) {
            if (direction === DirectionEnum.Outward) {
                if (this.booking.outward.passengers.length > 0) {
                    this.booking.outward.passengers = this.booking.outward.passengers.filter(p => p.id <= value || p.type !== type);
                }
            }
            else {
                if (this.booking.return.passengers.length > 0) {
                    this.booking.return.passengers = this.booking.outward.passengers.filter(p => p.id <= value || p.type !== type);
                }
            }
        }
    },
    persist: {
        enabled: true,
        strategies: [
            { storage: stateStorage, paths: ['booking'] },
        ],
    },
});